import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';

import { FullCalendarModule, FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, EventInput, EventClickArg, DateSelectArg, EventDropArg, DatesSetArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import huLocale from '@fullcalendar/core/locales/hu';

@Component({
  selector: 'app-calendar-view',
  standalone: true,
  imports: [FullCalendarModule],
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
})
export class CalendarViewComponent {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  /** Aktuális idő mínusz 1 óra, hogy az aktuális időpont a nézet közepén legyen */
  static calculateScrollTime(): string {
    const now = new Date();
    let h = now.getHours() - 1;
    if (h < 6) h = 6;
    if (h > 18) h = 18;
    return `${h.toString().padStart(2, '0')}:00:00`;
  }

  @Input() set events(value: EventInput[]) {
    this._events = value;
    this.calendarOptions = { ...this.calendarOptions, events: value };
  }
  get events(): EventInput[] {
    return this._events;
  }
  private _events: EventInput[] = [];

  @Input() set slotDuration(value: string) {
    if (value && value !== this._slotDuration) {
      this._slotDuration = value;
      this.calendarOptions = { ...this.calendarOptions, slotDuration: value };
    }
  }
  private _slotDuration = '00:15:00';

  @Output() dateSelect = new EventEmitter<DateSelectArg>();
  @Output() eventClick = new EventEmitter<EventClickArg>();
  @Output() eventDrop = new EventEmitter<EventDropArg>();
  @Output() eventResize = new EventEmitter<EventResizeDoneArg>();
  @Output() datesSet = new EventEmitter<DatesSetArg>();

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    locale: huLocale,
    initialView: 'timeGridWeek',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
    },
    buttonText: {
      today: 'Ma',
      month: 'Hónap',
      week: 'Hét',
      day: 'Nap',
      list: 'Lista',
    },
    height: '100%',
    // Munkaidő: hétfő–péntek, hogy a hétvégi napokra ne lehessen eseményt létrehozni
    businessHours: {
      daysOfWeek: [1, 2, 3, 4, 5],
      startTime: '06:00',
      endTime: '20:00',
    },
    selectable: true,
    selectConstraint: 'businessHours',
    editable: true,
    selectMirror: true,
    dayMaxEvents: true,
    weekNumbers: true,
    weekNumberFormat: { week: 'short' },
    nowIndicator: true,
    scrollTime: CalendarViewComponent.calculateScrollTime(),
    scrollTimeReset: false,
    slotMinTime: '06:00:00',
    slotMaxTime: '20:00:00',
    slotDuration: '00:15:00',
    allDayText: 'Egész nap',
    noEventsText: 'Nincs esemény',
    moreLinkText: 'további',
    events: [],

    select: (info: DateSelectArg) => this.dateSelect.emit(info),
    eventClick: (info: EventClickArg) => this.eventClick.emit(info),
    eventDrop: (info: EventDropArg) => this.eventDrop.emit(info),
    eventResize: (info: EventResizeDoneArg) => this.eventResize.emit(info),
    datesSet: (info: DatesSetArg) => this.datesSet.emit(info),

    // Reminder: fix időpont – csak a kezdési idő megjelenítése (tartam nélkül)
    eventContent: (arg) => {
      const calEvent = arg.event.extendedProps?.['calendarEvent'];
      if (calEvent?.source === 'reminder') {
        const start = arg.event.start;
        const timeStr = start
          ? `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`
          : '';

        const frame = document.createElement('div');
        frame.className = 'fc-event-main-frame';

        const timeEl = document.createElement('div');
        timeEl.className = 'fc-event-time';
        timeEl.textContent = timeStr;
        frame.appendChild(timeEl);

        const titleContainer = document.createElement('div');
        titleContainer.className = 'fc-event-title-container';
        const titleEl = document.createElement('div');
        titleEl.className = 'fc-event-title fc-sticky';
        titleEl.textContent = arg.event.title;
        titleContainer.appendChild(titleEl);
        frame.appendChild(titleContainer);

        return { domNodes: [frame] };
      }
      return true;
    },
  };

  /** Nézet váltás programozottan */
  changeView(viewName: string): void {
    const calendarApi = this.calendarComponent?.getApi();
    if (calendarApi) {
      calendarApi.changeView(viewName);
    }
  }
}
